import { Controller } from "@hotwired/stimulus"

/**
 * Client-side search, filter, pagination, and expandable detail
 * for the import validation errors table.
 *
 * All data is embedded in the DOM as JSON — no server round-trips.
 */
export default class extends Controller {
  static targets = [
    "search",
    "fieldFilter",
    "tbody",
    "pageInfo",
    "prevBtn",
    "nextBtn",
  ]
  static values = {
    errors: Array, // [{row, field, message, data}]
    perPage: { type: Number, default: 50 },
  }

  connect() {
    this.currentPage = 1
    this.currentField = ""
    this.searchQuery = ""
    this.expandedRows = new Set()
    this.render()
  }

  search() {
    this.searchQuery = this.searchTarget.value.toLowerCase().trim()
    this.currentPage = 1
    this.render()
  }

  filterByField(event) {
    const field = event.currentTarget.dataset.field || ""
    this.currentField = this.currentField === field ? "" : field

    // Update active tab styles
    this.fieldFilterTargets.forEach((btn) => {
      const isActive = btn.dataset.field === this.currentField
      // Keep these tokens in sync with ValidationErrorsPanelComponent#filter_pill_classes.
      btn.classList.toggle("bg-primary", isActive)
      btn.classList.toggle("text-primary-foreground", isActive)
      btn.classList.toggle("bg-muted", !isActive)
      btn.classList.toggle("text-muted-foreground", !isActive)
    })

    this.currentPage = 1
    this.render()
  }

  toggleRow(event) {
    const rowKey = event.currentTarget.dataset.rowKey
    if (this.expandedRows.has(rowKey)) {
      this.expandedRows.delete(rowKey)
    } else {
      this.expandedRows.add(rowKey)
    }
    this.render()
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--
      this.render()
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++
      this.render()
    }
  }

  get filteredErrors() {
    let errors = this.errorsValue

    if (this.currentField) {
      errors = errors.filter((e) => e.field === this.currentField)
    }

    if (this.searchQuery) {
      errors = errors.filter((e) => {
        const rowStr = String(e.row)
        const code = (e.data?.code || "").toLowerCase()
        return (
          rowStr.includes(this.searchQuery) ||
          code.includes(this.searchQuery) ||
          e.message.toLowerCase().includes(this.searchQuery)
        )
      })
    }

    return errors
  }

  get totalPages() {
    return Math.max(
      1,
      Math.ceil(this.filteredErrors.length / this.perPageValue),
    )
  }

  get paginatedErrors() {
    const start = (this.currentPage - 1) * this.perPageValue
    return this.filteredErrors.slice(start, start + this.perPageValue)
  }

  render() {
    // Clamp page
    if (this.currentPage > this.totalPages) this.currentPage = this.totalPages

    const errors = this.paginatedErrors
    const total = this.filteredErrors.length
    const start = (this.currentPage - 1) * this.perPageValue + 1
    const end = Math.min(this.currentPage * this.perPageValue, total)

    // Page info
    if (total === 0) {
      this.pageInfoTarget.textContent = "No errors match your search"
    } else {
      this.pageInfoTarget.textContent = `Showing ${start}–${end} of ${total} errors`
    }

    // Prev/Next buttons
    this.prevBtnTarget.disabled = this.currentPage <= 1
    this.nextBtnTarget.disabled = this.currentPage >= this.totalPages
    this.prevBtnTarget.classList.toggle("opacity-40", this.currentPage <= 1)
    this.nextBtnTarget.classList.toggle(
      "opacity-40",
      this.currentPage >= this.totalPages,
    )

    // Render rows
    let html = ""
    for (const error of errors) {
      const rowKey = `${error.row}-${error.field}`
      const isExpanded = this.expandedRows.has(rowKey)

      html += `
        <tr class="border-b border-border cursor-pointer hover:bg-danger/5 transition-colors"
            data-action="click->catalog--validation-errors#toggleRow"
            data-row-key="${rowKey}">
          <td class="py-2 px-3 text-muted-foreground">
            <span class="inline-flex items-center gap-1">
              <svg class="w-3.5 h-3.5 text-muted-foreground transition-transform ${isExpanded ? "rotate-90" : ""}"
                   fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
              ${error.row}
            </span>
          </td>
          <td class="py-2 px-3">
            <span class="inline-block px-1.5 py-0.5 rounded text-xs font-medium bg-danger/10 text-danger">${this.escapeHtml(error.field)}</span>
          </td>
          <td class="py-2 px-3 text-danger">${this.escapeHtml(error.message)}</td>
        </tr>`

      if (isExpanded && error.data) {
        html += `
        <tr class="bg-muted border-b border-border">
          <td colspan="3" class="py-3 px-3">
            <div class="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              ${this.renderDataFields(error.data)}
            </div>
          </td>
        </tr>`
      }
    }

    this.tbodyTarget.innerHTML = html
  }

  renderDataFields(data) {
    const fields = [
      // CONEK-3963 — populated on per-sheet validation errors so the user
      // can see which sheet a row came from on the Validate page.
      "sheet",
      "code",
      "kind",
      "status",
      "unit",
      "base_cost",
      "stock_type",
      "external_id",
    ]
    return fields
      .filter(
        (f) => data[f] !== undefined && data[f] !== null && data[f] !== "",
      )
      .map(
        (f) => `
        <div>
          <span class="text-muted-foreground font-medium">${this.escapeHtml(f)}:</span>
          <span class="ml-1 font-mono">${this.escapeHtml(String(data[f]))}</span>
        </div>
      `,
      )
      .join("")
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
